/*
 * <snap-slider>
 *
 * A minimal native CSS scroll-snap slider — the same track mechanism as the
 * Product Reels "swiper" (.swiper-wrapper / .swiper-slide, round
 * .snap-slider__nav buttons) but with no loop clones and no centered active
 * slide: the row starts at the left edge and scrolls normally. No library.
 *
 * Markup:
 *   <snap-slider class="snap-slider" style="--snap-cols-desktop:4;--snap-cols-mobile:2">
 *     <ul class="swiper-wrapper">
 *       <li class="swiper-slide">…</li>
 *     </ul>
 *     <button class="snap-slider__nav snap-slider__nav--prev">…</button>
 *     <button class="snap-slider__nav snap-slider__nav--next">…</button>
 *   </snap-slider>
 *
 * The nav buttons are shown only while the row actually overflows. Call
 * .refresh() after making the slider visible (e.g. a tab it lives in becomes
 * active) so overflow is re-measured.
 */
if (!customElements.get('snap-slider')) {
  class SnapSlider extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.track = this.querySelector('.swiper-wrapper');
      if (!this.track) return;
      this.slides = Array.prototype.slice.call(this.track.children);
      if (this.slides.length === 0) return;

      this.prevBtn = this.querySelector('.snap-slider__nav--prev');
      this.nextBtn = this.querySelector('.snap-slider__nav--next');

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollBySlide(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollBySlide(1));

      this._onScroll = () => this.updateNav();
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll);

      this.bindKeyboard();
      this.refresh();

      // Re-check after async layout shifts (webfont swap, lazy images).
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.refresh());
      window.addEventListener('load', () => this.refresh(), { once: true });
    }

    disconnectedCallback() {
      if (this._onScroll) window.removeEventListener('resize', this._onScroll);
    }

    // Distance from one slide's left edge to the next (slide width + gap).
    unitWidth() {
      if (!this.slides || this.slides.length === 0) return 0;
      if (this.slides.length < 2) return this.slides[0].offsetWidth;
      return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    }

    scrollBySlide(direction) {
      // scrollBy past the first/last slide simply clamps to the track's range.
      this.track.scrollBy({ left: direction * this.unitWidth(), behavior: 'smooth' });
    }

    hasOverflow() {
      return this.track.scrollWidth - this.track.clientWidth > 1;
    }

    // Arrows are only meaningful when the row actually overflows its container.
    updateNav() {
      const show = this.slides.length > 1 && this.hasOverflow();
      if (this.prevBtn) this.prevBtn.hidden = !show;
      if (this.nextBtn) this.nextBtn.hidden = !show;
    }

    refresh() {
      if (this.track) this.updateNav();
    }

    // Arrow keys act on this slider only while it's in the viewport, visible,
    // and no form field is focused.
    bindKeyboard() {
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        if (!this.track || this.offsetParent === null) return;
        const tag = document.activeElement && document.activeElement.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        const rect = this.getBoundingClientRect();
        const inView = rect.bottom > 0 && rect.top < window.innerHeight;
        if (!inView) return;
        e.preventDefault();
        this.scrollBySlide(e.key === 'ArrowLeft' ? -1 : 1);
      });
    }
  }

  customElements.define('snap-slider', SnapSlider);
}
